import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, entitlementsApi } from "@/api/client";
import type { UserAccessResponse } from "@/types/api";
import { decodeJwt } from "./jwt";
import { getProviderById, getProviders, refreshAccessToken } from "./oidc";
import { tokenStore } from "./tokenStore";
import { useAuth } from "./useAuth";

/**
 * Result of the post-login per-user access check.
 * - `checking` — request in flight
 * - `granted`  — user is entitled (or claim succeeded, or backend disabled)
 * - `denied`   — `not-entitled` (contact 3T) or `retryable` (transient)
 */
export type AccessCheckState =
  | { status: "checking" }
  | { status: "granted" }
  | { status: "denied"; kind: "not-entitled" | "retryable" };

// A successful check is cached in sessionStorage keyed to the current access
// token (sub + exp) so a reload doesn't re-hit the LM while the same token is
// still valid. A refreshed/expired token has a different exp → cache miss →
// re-check. sessionStorage (not localStorage) so it clears when the tab closes.

const GRANT_CACHE_KEY = "3t-bridge:auth:access-verified";

/** Identity of the current access token used to key the grant cache. */
function currentTokenIdentity(): { sub: string; exp: number } | null {
  const token = tokenStore.getAccessToken();
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload) return null;
  return { sub: payload.sub, exp: payload.exp };
}

/**
 * True when a prior grant is cached for the current, unexpired access token.
 * Exported for unit tests.
 */
export function isGrantCached(): boolean {
  const id = currentTokenIdentity();
  if (!id || Date.now() / 1000 >= id.exp) return false;
  try {
    const raw = sessionStorage.getItem(GRANT_CACHE_KEY);
    if (!raw) return false;
    const cached = JSON.parse(raw) as { sub?: unknown; exp?: unknown };
    return cached.sub === id.sub && cached.exp === id.exp;
  } catch {
    return false;
  }
}

function writeGrant(): void {
  const id = currentTokenIdentity();
  if (!id) return;
  try {
    sessionStorage.setItem(GRANT_CACHE_KEY, JSON.stringify(id));
  } catch {
    /* sessionStorage unavailable — caching is best-effort */
  }
}

function clearGrant(): void {
  try {
    sessionStorage.removeItem(GRANT_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Resolve the provider used at login (mirrors AuthContext's private resolver). */
function resolveProvider() {
  const id = tokenStore.getProviderId();
  if (id) {
    const provider = getProviderById(id);
    if (provider) return provider;
  }
  const all = getProviders();
  return all.length === 1 ? all[0] : null;
}

/**
 * Refresh the access token in place using the stored refresh token, persisting
 * the new tokens so the next request picks them up. Returns `true` on success.
 */
async function tryRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.getRefreshToken();
  const provider = resolveProvider();
  if (!refreshToken || !provider) return false;
  try {
    const tokens = await refreshAccessToken(provider, refreshToken);
    tokenStore.setAccessToken(tokens.access_token);
    if (tokens.refresh_token) tokenStore.setRefreshToken(tokens.refresh_token);
    if (tokens.id_token) tokenStore.setIdToken(tokens.id_token);
    else tokenStore.clearIdToken();
    return true;
  } catch {
    return false;
  }
}

/** Map a user-access response (following up with a claim when pending). */
async function evaluate(resp: UserAccessResponse): Promise<AccessCheckState> {
  if (resp.allowed) return { status: "granted" };
  if (resp.reason === "USER_NOT_ENTITLED") {
    return { status: "denied", kind: "not-entitled" };
  }
  if (resp.reason === "USER_IS_PENDING") {
    const claim = await entitlementsApi.claimEntitlement();
    return claim.result
      ? { status: "granted" }
      : { status: "denied", kind: "retryable" };
  }
  // Unknown reason — don't hard-fail on a value we don't recognize.
  return { status: "denied", kind: "retryable" };
}

const isUnauthorized = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 401;

/**
 * Run the check, with a single refresh-and-retry on a 401. Returns the resolved
 * state, or `"reauth"` when the user must re-authenticate (refresh failed or the
 * retry is still 401). Any non-401 failure resolves to a retryable denial.
 * Exported for unit tests.
 */
export async function resolveAccess(): Promise<AccessCheckState | "reauth"> {
  try {
    return await evaluate(await entitlementsApi.userAccess());
  } catch (err) {
    if (!isUnauthorized(err)) return { status: "denied", kind: "retryable" };
    if (!(await tryRefresh())) return "reauth";
    try {
      return await evaluate(await entitlementsApi.userAccess());
    } catch (retryErr) {
      return isUnauthorized(retryErr)
        ? "reauth"
        : { status: "denied", kind: "retryable" };
    }
  }
}

/**
 * Runs the per-user access check once on mount and exposes a `retry`. A cached
 * grant for the current token short-circuits the network call.
 *
 * On a 401 (expired/invalid access token) it refreshes once and retries; if the
 * refresh fails or the retry is still 401, it logs the user out to re-authenticate.
 * Any other failure (502, network) resolves to a retryable denial.
 */
export function useUserAccessCheck(): {
  state: AccessCheckState;
  retry: () => void;
} {
  const { logout } = useAuth();
  const [state, setState] = useState<AccessCheckState>(() =>
    isGrantCached() ? { status: "granted" } : { status: "checking" },
  );

  // Guard against state updates / logout firing after the gate unmounts
  // mid-check (e.g. the user signs out during "Verifying access…").
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Prevent overlapping checks (e.g. rapid "Try again") from firing concurrent
  // requests — notably a duplicate, state-mutating claim-user-entitlement.
  const inFlightRef = useRef(false);

  // Persist a grant / drop a stale one alongside the state transition. The
  // cache write is safe post-unmount; only the state update is guarded.
  const settle = useCallback((next: AccessCheckState) => {
    // settle only receives a resolved state (granted | denied), never "checking".
    if (next.status === "granted") writeGrant();
    else clearGrant();
    if (mountedRef.current) setState(next);
  }, []);

  const run = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setState({ status: "checking" });
    try {
      const result = await resolveAccess();
      if (result === "reauth") {
        if (mountedRef.current) logout();
      } else {
        settle(result);
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [logout, settle]);

  // On mount, run the check unless a valid grant is cached. `run` is stable and
  // `inFlightRef` makes it idempotent, so this fires once and no-ops on
  // StrictMode's dev double-invoke. `retry` forces a fresh check.
  useEffect(() => {
    if (!isGrantCached()) void run();
  }, [run]);

  return { state, retry: () => void run() };
}
